import { camelCase } from 'lodash';
import naturalCompare from 'natural-compare-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
// import { useSelector } from 'react-redux';
import { FieldSectionType } from 'api/field-sections/types';
// import { CompanyId, ProjectId, UserStatus, UserType } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { // FieldDataType,
KeyEventsAndIntervalFieldTypes } from 'components/organisms/module-settings/fields/types';
import { FieldType, filterableFieldType, SourceType } from 'components/organisms/tasks/fields';
// import { RootState } from 'data';
import { useDoesPartnerMatchPermissionName, useIsCurrentUserPartner } from 'hooks/use-partner';
// import { useUser } from 'hooks/use-user';
import { useIntervalsFieldsQueryV2, useKeyEventsFieldsQueryV2, useProjectFieldsQuery, useProjectFieldsQueryV2 } from 'queries';
import { useProjectsFromInitData } from 'queries/account';
// import { useUsersQuery } from 'queries/members';
// import { constructUserMeta } from 'stores/fields/util';
// import { UsersStore } from 'stores/users';
// import { userOption } from './construct-user-option';
export const default_project_fields = [
    'projectName',
    'startDate',
    'dueDate',
    'customer',
    'projectOwner',
    'projectId',
    'projectFee',
    'inferredProgress',
    'currency',
    'customerChampion',
    'phaseId',
    'annualizedRecurringRevenue',
    'budgetedMinutes'
];
const feature_specific_project_fields = [
    'budgetedHours'
];
const getSourceType = (field)=>{
    if (field.objectType === FieldSectionType.Project) {
        return SourceType.Project;
    }
    if (field.objectType === FieldSectionType.Users) {
        return SourceType.User;
    }
    return SourceType.Task;
};
export const nonDefaultField = (f)=>!default_project_fields.includes(camelCase(f.fieldName));
const statusField = (f)=>f.customField && f.fieldName.toLowerCase() === 'status';
const nonFeatureField = (f)=>!feature_specific_project_fields.includes(camelCase(f.fieldName));
export const isSelectTypeField = (field)=>{
    const type = filterableFieldType(field);
    return [
        FieldType.SingleSelect,
        FieldType.MultiSelect,
        FieldType.SingleUser,
        FieldType.MultiUser
    ].includes(type);
};
export const useProjectFieldByName = (fieldName)=>{
    const { data } = useProjectFieldsQuery();
    return useMemo(()=>data === null || data === void 0 ? void 0 : data.find((field)=>{
            var _field_fieldName;
            return (field === null || field === void 0 ? void 0 : (_field_fieldName = field.fieldName) === null || _field_fieldName === void 0 ? void 0 : _field_fieldName.toLowerCase()) === (fieldName === null || fieldName === void 0 ? void 0 : fieldName.toLowerCase()) && (field === null || field === void 0 ? void 0 : field.objectType) === FieldSectionType.Project;
        }), [
        data,
        fieldName
    ]);
};
export const useProjectFields = ()=>{
    var _projectData_projects;
    const { data, isLoading: isProjectFieldsLoading } = useProjectFieldsQueryV2();
    const { data: projectData } = useProjectsFromInitData();
    // const { data: allUsers } = useUsersQuery();
    // const { user: currentUser } = useUser();
    const projectIdsFromQuery = projectData === null || projectData === void 0 ? void 0 : (_projectData_projects = projectData.projects) === null || _projectData_projects === void 0 ? void 0 : _projectData_projects.map((x)=>x.projectId);
    const [projectIds, setProjectIds] = useState(projectIdsFromQuery);
    useEffect(()=>{
        // tried using useSelector with memozing it didn't help since it recomputed the projectid.
        flushSync(()=>{
            setProjectIds((old)=>{
                if ((old === null || old === void 0 ? void 0 : old.length) === (projectIdsFromQuery === null || projectIdsFromQuery === void 0 ? void 0 : projectIdsFromQuery.length)) {
                    return old;
                }
                return projectIdsFromQuery;
            });
        });
    }, [
        projectIdsFromQuery
    ]);
    // const membershipsStore = useSelector((store: RootState) => store.memberships);
    // const usersStore = useSelector((store: RootState) => store.users);
    const [loading, setIsLoading] = useState(false);
    const [fields, setFields] = useState();
    const getComputedData = useCallback(()=>{
        return new Promise((res)=>{
            setIsLoading(true);
            queueMicrotask(()=>{
                const dataTemp = ()=>{
                    if (data == null || !projectIds) {
                        return undefined;
                    }
                    return data === null || data === void 0 ? void 0 : data.fields.reduce((acc, field)=>{
                        if (nonFeatureField(field)) {
                            acc.push({
                                ...field,
                                sourceType: getSourceType(field)
                            });
                        }
                        return acc;
                    }, []).sort((fieldA, fieldB)=>naturalCompare(fieldA.fieldLabel || '', fieldB.fieldLabel || ''));
                };
                res(dataTemp());
            });
        });
    }, [
        data,
        projectIds
    ]);
    useEffect(()=>{
        (async ()=>{
            getComputedData().then((data2)=>{
                setIsLoading(false);
                setFields(data2);
            });
        })();
    }, [
        getComputedData
    ]);
    return {
        data: fields,
        isLoading: fields == null || loading || isProjectFieldsLoading
    };
};
export const useProjectCustomFields = (filterFieldsBy = nonDefaultField)=>{
    const isCurrentUserPartner = useIsCurrentUserPartner();
    const isPermissivePartner = useDoesPartnerMatchPermissionName(PartnerPermissionName.Permissive);
    const { data, isLoading } = useProjectFields();
    const fields = useMemo(()=>{
        if (!isLoading) {
            if (isCurrentUserPartner && !isPermissivePartner) {
                // Show only status field & shared custom fields to MODERATE & RESTRICTED partners
                return (data !== null && data !== void 0 ? data : []).filter((f)=>statusField(f) || !f.private);
            }
            return (data !== null && data !== void 0 ? data : []).filter(filterFieldsBy);
        }
        return [];
    }, [
        isCurrentUserPartner,
        isPermissivePartner,
        isLoading,
        data,
        filterFieldsBy
    ]);
    return {
        data: fields,
        isLoading
    };
};
export const useKeyEvents = ()=>{
    var _ref;
    var _data_fields;
    const { data } = useKeyEventsFieldsQueryV2();
    return (_ref = data === null || data === void 0 ? void 0 : (_data_fields = data.fields) === null || _data_fields === void 0 ? void 0 : _data_fields.filter((field)=>{
        var _field_metaData;
        return field.fieldType === KeyEventsAndIntervalFieldTypes.KeyEvents && ((_field_metaData = field.metaData) === null || _field_metaData === void 0 ? void 0 : _field_metaData.relatedFieldId);
    })) !== null && _ref !== void 0 ? _ref : [];
};
export const useAllKeyEvents = ()=>{
    var _ref;
    var _data_fields;
    const { data } = useKeyEventsFieldsQueryV2();
    return (_ref = data === null || data === void 0 ? void 0 : (_data_fields = data.fields) === null || _data_fields === void 0 ? void 0 : _data_fields.filter((field)=>field.fieldType === KeyEventsAndIntervalFieldTypes.KeyEvents)) !== null && _ref !== void 0 ? _ref : [];
};
export const useIntervals = ({ enabled = true } = {})=>{
    var _ref;
    var _data_fields;
    const { data } = useIntervalsFieldsQueryV2({
        enabled
    });
    return (_ref = data === null || data === void 0 ? void 0 : (_data_fields = data.fields) === null || _data_fields === void 0 ? void 0 : _data_fields.filter((field)=>{
        var _field_metaData;
        return field.fieldType === KeyEventsAndIntervalFieldTypes.Intervals && ((_field_metaData = field.metaData) === null || _field_metaData === void 0 ? void 0 : _field_metaData.relatedFieldId);
    })) !== null && _ref !== void 0 ? _ref : [];
};
export const useAllIntervals = ({ enabled = true } = {})=>{
    var _ref;
    var _data_fields;
    const { data } = useIntervalsFieldsQueryV2({
        enabled
    });
    return (_ref = data === null || data === void 0 ? void 0 : (_data_fields = data.fields) === null || _data_fields === void 0 ? void 0 : _data_fields.filter((field)=>field.fieldType === KeyEventsAndIntervalFieldTypes.Intervals)) !== null && _ref !== void 0 ? _ref : [];
};
